import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Image } from '@codes/ui-kit/components/ui/image';
import { PriceTable } from '@lemoncloud/codes-backend-api';
import { CatalogView } from '@lemoncloud/codes-goods-api';
import {
    CatalogImageSlider,
    Editor,
    Nav,
    OrderProductButton,
    OrderProductDialog,
    getPriceLabel,
    useStereoIcon,
} from '@web/app/shared';
import { createYooptaEditor } from '@yoopta/editor';
import { markdown } from '@yoopta/exports';

import { useFetchCatalogById } from '@codes/catalog';
import { SharedSpinner } from '@codes/ui-kit';

const mockEurekaCatalog = {
    id: '387',
    createdAt: 1746669104577,
    updatedAt: 1746670097749,
    deletedAt: 0,
    $: {
        sid: '10008',
        uid: '1000007',
    },
    stereo: 'solution',
    name: '유레카키트',
    nameEn: 'EurekaKit',
    ownerId: '1000007',
    owner$: {
        id: '1000007',
        name: 'Sunny경효선',
    },
    thumbs: ['https://image.lemoncloud.io/c7fe9b2c-e6e4-4149-9cf7-3aeae88dbbe0'],
    images: ['https://image.lemoncloud.io/0ab4f78d-d04e-4565-bc6b-71a2ed8ac769'],
    summary: '즉시 사용 가능한 앱 및 백엔드 코드로 MVP 개발 가속화',
    summaryEn: 'Accelerate MVP development with ready-to-use app and backend code',
    desc: 'React Native 앱, 관리자 페이지, 서버리스 API, 클라우드 백엔드, 개발 도구',
    descEn: 'React Native apps, admin portals, serverless APIs, cloud backend, development tools',
    readme: '유레카키트는 React Native 앱, 관리자 시스템, 서버리스 API 등 필수 코드 모듈을 제공하여 MVP를 빠르게 구축할 수 있는 개발 키트입니다. 즉시 사용 가능한 코드 컴포넌트로 개발 시간과 비용을 대폭 절감하며, 비개발자도 쉽게 맞춤형 서비스를 구현할 수 있습니다.',
    readmeEn:
        'EurekaKit is a development kit that offers essential code modules for React Native apps, admin systems, and serverless APIs to rapidly build MVPs. With ready-to-use components, it significantly reduces development time and costs, allowing even non-developers to easily implement customized services.',
    dependencyIds: [],
    dependency$$: [],
    hidden: false,
    isDomestic: true,
    price$: {
        initPrice: 9533,
        currency: 0,
        trafficPrice: 0,
        hourPerPrice: 0,
        callPerPrice: 0,
    },
};
export const DetailPage = () => {
    const { t } = useTranslation();
    const { i18n } = useTranslation();
    const { catalogId } = useParams();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [exchangeRate, setExchangeRate] = useState(1416.2); // TODO: 환율 정보 받아오기

    const getStereoIcon = useStereoIcon();

    const { data: fetchedCatalog, isLoading: isFetchLoading } = useFetchCatalogById(catalogId);

    const isEureka = catalogId === 'eurekakit';
    const currentCatalog = isEureka ? mockEurekaCatalog : fetchedCatalog;

    const isFetching = isEureka ? false : isFetchLoading;

    useEffect(() => {
        if (!currentCatalog) {
            return;
        }

        const content =
            i18n.language === 'en'
                ? currentCatalog.readmeEn || currentCatalog.readme || ''
                : currentCatalog.readme || currentCatalog.readmeEn || '';

        const value = markdown.deserialize(yooptaEditor, content);
        yooptaEditor.setEditorValue(value);
    }, [currentCatalog, i18n.language]);

    const textRef = useRef<HTMLParagraphElement>(null);
    const noticeRef = useRef<HTMLUListElement>(null);

    const yooptaEditor = useMemo(() => createYooptaEditor(), []);

    const priceKeys: (keyof PriceTable)[] = ['initPrice', 'trafficPrice', 'hourPerPrice', 'callPerPrice'];

    const contentList = [
        t('guide.callbackNotice'), // '해당 서비스는 신청 후에 확인차 고객센터에서 전화드릴 예정입니다.'
        t('guide.approvalNotice'), // '관리자 승인 완료 후에 서비스 이용이 가능합니다.'
        t('guide.cancellationNotice'), // '서비스 신청 취소는 승인 전까지 가능하며, 서비스 신청 후에 신청 상세페이지에서 내역 조회 및 취소 가능합니다.'
    ];

    const handleAddServiceClick = () => {
        setIsDialogOpen(true);
    };

    const getIconImage = (catalog: CatalogView | undefined) => {
        if (!catalog) {
            return '/assets/icons/empty.svg';
        }

        const thumb = catalog.thumbs && catalog.thumbs.length >= 1 ? catalog.thumbs[0] : null;

        if (thumb) {
            return thumb;
        }

        return getStereoIcon(catalog.stereo as string);
    };

    return isFetching ? (
        <div className="h-screen flex justify-center items-center">
            <SharedSpinner />
        </div>
    ) : (
        <>
            <Nav title={(i18n.language === 'en' && currentCatalog.nameEn) || currentCatalog.name} />
            <div className="pb-[120px] bg-background flex justify-center">
                <div className="max-w-2xl w-full">
                    <div className="px-6 flex-1">
                        <div className="flex items-center gap-2 mb-[13px] pt-8">
                            <div className="w-9 h-9 rounded-full bg-avatar border border-card-border flex items-center justify-center p-1">
                                <Image
                                    src={getStereoIcon(currentCatalog.stereo)}
                                    darkSrc={getStereoIcon(currentCatalog.stereo)}
                                    alt={currentCatalog.stereo}
                                />
                            </div>
                            <div className="text-[18px] font-semibold">
                                {(i18n.language === 'en' && currentCatalog.nameEn) || currentCatalog.name}
                            </div>
                        </div>
                        <div className="text-sub-title mb-[13px] break-words max-xs:text-sm">
                            {(i18n.language === 'en' && currentCatalog.summaryEn) || currentCatalog.summary}
                        </div>
                        <CatalogImageSlider
                            images={currentCatalog.images}
                            name={currentCatalog.name}
                            stereo={currentCatalog.stereo}
                            getIconByStereo={() => getStereoIcon(currentCatalog.stereo)}
                        />
                        <div className="w-full h-[1px] bg-divider my-6"></div>
                        <div className="text-[18px] font-semibold mb-4">{t('serviceDetail.overview')}</div>
                        <div>
                            <p ref={textRef} className="text-sub-title break-words max-xs:text-sm">
                                {(i18n.language === 'en' && currentCatalog.descEn) || currentCatalog.desc}
                            </p>
                        </div>
                        <div className="w-full h-[1px] bg-divider my-6"></div>
                        <div className="text-[18px] font-semibold mb-4">README</div>
                        <Editor editor={yooptaEditor} readOnly={true} />
                        <div className="w-full h-[1px] bg-divider my-6"></div>
                        {currentCatalog.price$ ? (
                            <>
                                <div className="text-[18px] font-semibold mb-4">{t('serviceDetail.serviceFee')}</div>
                                <div className="bg-box rounded-[14px] px-6 py-[18px]">
                                    {priceKeys.filter(key => currentCatalog.price$[key] > 0).length > 0 ? (
                                        <div className="space-y-2">
                                            {priceKeys
                                                .filter(key => currentCatalog.price$[key] > 0)
                                                .map(key => (
                                                    <div
                                                        key={key}
                                                        className="flex justify-between items-center font-medium"
                                                    >
                                                        <span className="text-base max-xs:text-sm text-sub-title">
                                                            {getPriceLabel(key, i18n.language)}
                                                        </span>
                                                        <span className="text-base max-xs:text-sm">
                                                            {i18n.language === 'en'
                                                                ? `$${currentCatalog.price$[key].toLocaleString()}`
                                                                : `₩${Math.floor(
                                                                      currentCatalog.price$[key] * exchangeRate
                                                                  ).toLocaleString()}`}
                                                        </span>
                                                    </div>
                                                ))}
                                        </div>
                                    ) : (
                                        <div className="text-center text-sub-title">{t('serviceDetail.noPrice')}</div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-center text-sub-title">{t('serviceDetail.noPrice')}</div>
                        )}
                        <div className="w-full h-[1px] bg-divider my-6"></div>
                        <div className="text-sm mb-[10px] font-medium text-sub-title">
                            {t('serviceDetail.serviceGuidelines')}
                        </div>
                        <div className="pb-20">
                            <ul ref={noticeRef} className="flex flex-col gap-[3px]">
                                {contentList.map((content, index) => (
                                    <li key={index} className="relative pl-2 text-xs text-placeholder">
                                        <span className="absolute left-0 top-1/2 transform -translate-y-1/2 w-[3px] h-[3px] bg-outline-text rounded-full"></span>
                                        {content}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <OrderProductButton onAddServiceClick={handleAddServiceClick} />
                    </div>
                </div>
                <OrderProductDialog catalog={currentCatalog} isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} />
            </div>
        </>
    );
};
